import { useState } from 'react';
import {
  ShieldAlert,
  Plus,
  Calendar,
  Pencil,
  Trash2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Check,
  Tag,
} from 'lucide-react';
import Button from '@components/ui/Button.js';
import Dialog from '@components/ui/Dialog.js';
import { cn } from '@utils/Cn.js';
import { formatDate } from '@repo/utils';
import {
  CLAIM_STATUS_LABELS,
} from '@repo/constants';
import type { PolicyClaim, ClaimStatus } from '../types/index.js';
import {
  usePolicyClaimsQuery,
  useDeletePolicyClaimMutation,
} from '../hooks/usePolicyClaimsQuery.js';
import AddEditClaimModal from './AddEditClaimModal.js';

interface PolicyClaimsSectionProps {
  policyId: string;
}

const statusToneMap: Record<ClaimStatus, { bg: string; text: string; border: string; dot: string; icon: typeof Clock }> = {
  CLAIM_INITIATED: {
    bg: 'bg-amber-500/10 dark:bg-amber-950/40',
    text: 'text-amber-800 dark:text-amber-300',
    border: 'border-amber-500/30',
    dot: 'bg-amber-500',
    icon: Clock,
  },
  APPROVED: {
    bg: 'bg-emerald-500/10 dark:bg-emerald-950/40',
    text: 'text-emerald-800 dark:text-emerald-300',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-500',
    icon: CheckCircle2,
  },
  REJECTED: {
    bg: 'bg-rose-500/10 dark:bg-rose-950/40',
    text: 'text-rose-800 dark:text-rose-300',
    border: 'border-rose-500/30',
    dot: 'bg-rose-500',
    icon: XCircle,
  },
  SETTLED: {
    bg: 'bg-cyan-500/10 dark:bg-cyan-950/40',
    text: 'text-cyan-800 dark:text-cyan-300',
    border: 'border-cyan-500/30',
    dot: 'bg-cyan-500',
    icon: Check,
  },
  CLOSED: {
    bg: 'bg-slate/10 dark:bg-slate/20',
    text: 'text-slate dark:text-slate-300',
    border: 'border-slate/30',
    dot: 'bg-slate',
    icon: Check,
  },
};

export default function PolicyClaimsSection({ policyId }: PolicyClaimsSectionProps) {
  const { data: claimsRes, isLoading } = usePolicyClaimsQuery(policyId);
  const deleteMutation = useDeletePolicyClaimMutation(policyId);

  const claims = claimsRes?.data ?? [];

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<PolicyClaim | null>(null);
  const [claimToDelete, setClaimToDelete] = useState<PolicyClaim | null>(null);

  // Financial summary
  const totalClaimed = claims.reduce((acc, c) => acc + (c.claimAmount ? Number(c.claimAmount) : 0), 0);
  const totalApproved = claims.reduce((acc, c) => acc + (c.approvedAmount ? Number(c.approvedAmount) : 0), 0);
  const totalSettled = claims.reduce((acc, c) => acc + (c.settledAmount ? Number(c.settledAmount) : 0), 0);

  const handleOpenAdd = () => {
    setSelectedClaim(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (claim: PolicyClaim) => {
    setSelectedClaim(claim);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!claimToDelete) return;
    deleteMutation.mutate(claimToDelete.id, {
      onSuccess: () => {
        setClaimToDelete(null);
      },
    });
  };

  return (
    <section className="bg-surface border border-line rounded-[20px] sm:rounded-[24px] p-5 sm:p-6 lg:p-7 shadow-[0_4px_24px_rgba(15,23,42,0.06)] text-left min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 border-b border-line/80">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 shrink-0">
            <ShieldAlert size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-ink leading-tight">Policy Claims Tracking</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-paper text-ink-soft border border-line">
                {claims.length} {claims.length === 1 ? 'Claim' : 'Claims'}
              </span>
            </div>
            <p className="text-xs text-ink-faint mt-0.5">
              Track multiple claims against this policy with dates, status updates, and settlements.
            </p>
          </div>
        </div>

        <Button
          variant="primary"
          size="sm"
          className="flex items-center gap-1.5 self-start sm:self-auto"
          onClick={handleOpenAdd}
        >
          <Plus size={14} strokeWidth={2.5} />
          <span>Record Claim</span>
        </Button>
      </div>

      {/* Financial Summary Metric Chips */}
      {claims.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-5">
          <div className="rounded-xl border border-line bg-paper/60 p-3.5 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-ink-faint uppercase tracking-wider">Total Claimed</span>
            <span className="text-lg font-black text-ink mt-1">
              ₹{totalClaimed.toLocaleString('en-IN')}
            </span>
          </div>

          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3.5 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider">Total Approved</span>
            <span className="text-lg font-black text-emerald-700 dark:text-emerald-300 mt-1">
              ₹{totalApproved.toLocaleString('en-IN')}
            </span>
          </div>

          <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/5 p-3.5 flex flex-col justify-between">
            <span className="text-[11px] font-bold text-cyan-800 dark:text-cyan-300 uppercase tracking-wider">Total Settled</span>
            <span className="text-lg font-black text-cyan-700 dark:text-cyan-300 mt-1">
              ₹{totalSettled.toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      )}

      {/* Claims Responsive Grid (Matching Policy Documents layout: 1 col mobile, 2 cols lg, 3 cols xl) */}
      <div className="mt-4">
        {isLoading ? (
          <div className="py-8 text-center text-xs text-ink-faint">Loading claims...</div>
        ) : claims.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line-strong p-8 text-center bg-paper/30 flex flex-col items-center justify-center">
            <div className="h-11 w-11 rounded-2xl bg-paper flex items-center justify-center text-ink-faint border border-line mb-3">
              <ShieldAlert size={20} />
            </div>
            <h4 className="text-sm font-bold text-ink">No claims recorded yet</h4>
            <p className="text-xs text-ink-faint max-w-sm mt-1 mb-4">
              If the client files a claim for this policy term, record it here to track review progress and payout status.
            </p>
            <Button variant="outline" size="sm" onClick={handleOpenAdd} className="flex items-center gap-1.5">
              <Plus size={13} />
              <span>Record First Claim</span>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 pt-1">
            {claims.map((claim) => {
              const tone = statusToneMap[claim.claimStatus] ?? statusToneMap.CLAIM_INITIATED;
              const StatusIcon = tone.icon;

              return (
                <div
                  key={claim.id}
                  className="flex flex-col justify-between gap-3 p-4 rounded-xl border border-line bg-paper/60 hover:bg-paper hover:border-slate/40 hover:shadow-md transition-all group"
                >
                  {/* Top Header of Card */}
                  <div className="flex items-center justify-between gap-2 pb-2.5 border-b border-line/60">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide border',
                        tone.bg,
                        tone.text,
                        tone.border,
                      )}
                    >
                      <StatusIcon size={11} className="shrink-0" />
                      <span>{CLAIM_STATUS_LABELS[claim.claimStatus] ?? claim.claimStatus}</span>
                    </span>

                    <span className="text-[11px] font-bold text-ink flex items-center gap-1">
                      <Calendar size={11} className="text-slate shrink-0" />
                      {formatDate(claim.claimDate)}
                    </span>
                  </div>

                  {/* Body Info */}
                  <div className="space-y-2.5 text-xs flex-1">
                    {/* Meta Tags */}
                    {(claim.claimNumber || claim.claimType || claim.incidentDate) && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {claim.claimNumber && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface border border-line text-ink text-[11px] font-mono font-bold">
                            <Tag size={10} className="text-ink-faint" />
                            <span>{claim.claimNumber}</span>
                          </span>
                        )}
                        {claim.claimType && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-surface border border-line text-ink-soft text-[11px] font-medium">
                            {claim.claimType}
                          </span>
                        )}
                        {claim.incidentDate && (
                          <span className="text-[11px] text-ink-faint">
                            Incident: {formatDate(claim.incidentDate)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Dynamic Amounts (Only shown when at least one amount is entered) */}
                    {(claim.claimAmount != null || claim.approvedAmount != null || claim.settledAmount != null) && (
                      <div className="flex items-center justify-between gap-2 bg-surface/90 rounded-lg px-2.5 py-1.5 border border-line/60">
                        {claim.claimAmount != null && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-bold text-ink-faint uppercase">Claimed:</span>
                            <span className="text-xs font-black text-ink">
                              ₹{Number(claim.claimAmount).toLocaleString('en-IN')}
                            </span>
                          </div>
                        )}
                        {claim.settledAmount != null ? (
                          <div className="flex items-center gap-1.5 ml-auto">
                            <span className="text-[10px] font-bold text-cyan-800 dark:text-cyan-400 uppercase">Settled:</span>
                            <span className="text-xs font-black text-cyan-700 dark:text-cyan-400">
                              ₹{Number(claim.settledAmount).toLocaleString('en-IN')}
                            </span>
                          </div>
                        ) : claim.approvedAmount != null ? (
                          <div className="flex items-center gap-1.5 ml-auto">
                            <span className="text-[10px] font-bold text-emerald-800 dark:text-emerald-400 uppercase">Approved:</span>
                            <span className="text-xs font-black text-emerald-700 dark:text-emerald-400">
                              ₹{Number(claim.approvedAmount).toLocaleString('en-IN')}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    )}

                    {/* Rejection Notice */}
                    {claim.claimStatus === 'REJECTED' && claim.rejectionReason && (
                      <div className="rounded-lg bg-rose-500/10 border border-rose-500/25 p-2 text-[11px] flex items-start gap-1.5">
                        <AlertCircle size={12} className="text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <span className="font-bold text-rose-800 dark:text-rose-300">Reason: </span>
                          <span className="text-rose-900 dark:text-rose-200 break-words">{claim.rejectionReason}</span>
                        </div>
                      </div>
                    )}

                    {/* Remarks */}
                    {claim.remarks && (
                      <div className="rounded-lg bg-surface/60 border border-line/40 p-1.5 text-[11px] text-ink-soft">
                        <span className="font-bold text-ink-faint mr-1 uppercase text-[9px]">Notes:</span>
                        <span className="line-clamp-2">{claim.remarks}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer Actions */}
                  <div className="flex items-center justify-end gap-2 pt-2.5 border-t border-line/60">
                    <button
                      type="button"
                      onClick={() => handleOpenEdit(claim)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-line bg-surface text-xs font-semibold text-ink-soft hover:text-slate hover:border-slate/40 hover:bg-slate/10 transition-all cursor-pointer"
                      title="Edit Claim"
                    >
                      <Pencil size={12} />
                      <span>Edit</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setClaimToDelete(claim)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-line bg-surface text-xs font-semibold text-ink-soft hover:text-rose-600 hover:border-rose-500/40 hover:bg-rose-50 dark:hover:bg-rose-950/45 transition-all cursor-pointer"
                      title="Delete Claim"
                    >
                      <Trash2 size={12} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Claim Modal */}
      <AddEditClaimModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedClaim(null);
        }}
        policyId={policyId}
        claim={selectedClaim}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!claimToDelete}
        onClose={() => setClaimToDelete(null)}
        title="Delete Claim Record?"
        description="Are you sure you want to delete this claim record? This action cannot be undone."
      >
        <div className="flex items-center justify-end gap-3 pt-4">
          <Button variant="outline" size="sm" onClick={() => setClaimToDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Claim'}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}
